import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConsentPicker } from "@/components/ConsentPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PenLine, Copy, Send, XCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const TOKENS = [{ value: "{nome}", label: "Nome destinatario" }, { value: "{link}", label: "Link firma" }, { value: "{cliente}", label: "Nome cliente" }, { value: "__fixed__", label: "Testo fisso" }];
const isTok = (v) => ["{nome}", "{link}", "{cliente}"].includes(v);
const copy = (link) => navigator.clipboard.writeText(link).then(() => toast.success("Link copiato"));
const STATUS = { inviato: ["PDF inviato", "bg-sky-100 text-sky-800"], caricato: ["Firmato caricato", "bg-emerald-100 text-emerald-800"], in_attesa: ["In attesa", "bg-amber-100 text-amber-800"], completamento: ["In completamento", "bg-sky-100 text-sky-800"], completata: ["Firmata", "bg-emerald-100 text-emerald-800"], annullata: ["Annullata", "bg-slate-200 text-slate-700"], scaduta: ["Scaduta", "bg-rose-100 text-rose-800"] };

const WaPicker = ({ wa, setWa, preferred }) => {
  const [tpls, setTpls] = useState(null);
  useEffect(() => { api.get("/whatsapp/templates").then((r) => setTpls(r.data.filter((t) => t.supported))).catch(() => setTpls([])); }, []);
  const key = (t) => `${t.account_id}|${t.name}|${t.language}`;
  const build = (t) => ({ ...t, params: Array.from({ length: t.vars }, (_, i) => (i === t.vars - 1 ? "{link}" : i === 0 ? "{nome}" : t.vars === 3 && i === 1 ? "{cliente}" : "")) });
  const pick = (k) => setWa(build(tpls.find((x) => key(x) === k)));
  useEffect(() => {
    const t = (tpls || []).find((x) => x.name === preferred);
    if (t) setWa(build(t));
  }, [tpls, preferred]); // eslint-disable-line react-hooks/exhaustive-deps
  const setP = (i, v) => setWa({ ...wa, params: wa.params.map((p, j) => (j === i ? v : p)) });
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <Select value={wa ? key(wa) : undefined} onValueChange={pick} disabled={!tpls?.length}>
        <SelectTrigger data-testid="sign-wa-template"><SelectValue placeholder={tpls === null ? "Caricamento…" : tpls.length ? "Modello WhatsApp approvato" : "Nessun modello disponibile"} /></SelectTrigger>
        <SelectContent>{(tpls || []).map((t) => <SelectItem key={key(t)} value={key(t)}>{t.name} · {t.category}</SelectItem>)}</SelectContent>
      </Select>
      {wa && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{wa.body}</p>}
      {wa?.params.map((p, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`sign-wa-param-${i + 1}`}>
          <span className="w-10 text-xs font-mono text-muted-foreground">{`{{${i + 1}}}`}</span>
          <Select value={isTok(p) ? p : "__fixed__"} onValueChange={(v) => setP(i, v === "__fixed__" ? "" : v)}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{TOKENS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          {!isTok(p) && <Input value={p} onChange={(e) => setP(i, e.target.value)} placeholder="Testo fisso" />}
        </div>
      ))}
      {wa && !wa.params.includes("{link}") && <p className="text-xs text-amber-600">Inserisci il «Link firma» in una delle variabili.</p>}
    </div>
  );
};

const SendDialog = ({ practice, onClose, onSent }) => {
  const [tpls, setTpls] = useState([]);
  const [all, setAll] = useState([]);
  const [mode, setMode] = useState("firma");
  const stampa = mode === "stampa";
  const [sel, setSel] = useState([]);
  const [conv, setConv] = useState(!!practice.convenzionato_id);
  const [coob, setCoob] = useState(true);
  const [coobEmail, setCoobEmail] = useState("");
  const [coobPhone, setCoobPhone] = useState("");
  const [email, setEmail] = useState(practice.email || "");
  const [phone, setPhone] = useState(practice.telefono || "");
  const [byEmail, setByEmail] = useState(true);
  const [byWa, setByWa] = useState(false);
  const [wa, setWa] = useState(null);
  const [cons, setCons] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/doc-templates").then((r) => setAll(r.data)); }, []);
  useEffect(() => {
    const l = all.filter((t) => (stampa ? t.fields.length : t.fields.some((f) => f.key === "firma_cliente")));
    setTpls(l); setSel(l.map((t) => t.id));
  }, [all, stampa]);
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);

  const send = async () => {
    if (!sel.length) { toast.error("Seleziona almeno un documento"); return; }
    if (byWa && (!wa || !wa.params.includes("{link}") || wa.params.some((p) => !p.trim()))) { toast.error("Completa il messaggio WhatsApp con il link firma"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/practices/${practice.id}/sign-requests`, {
        template_ids: sel, mode, include_convenzionato: conv, include_coobbligato: coob, coob_email: coobEmail, coob_phone: coobPhone, consents: Object.fromEntries(sel.map((id) => [id, cons[id] || {}])), send_email: byEmail, cliente_email: email, cliente_phone: phone,
        wa: byWa ? { template_name: wa.name, template_language: wa.language, account_id: wa.account_id, params: wa.params } : null });
      const sent = data.signers.map((s) => `${s.name}: ${s.sent.length ? s.sent.join(" + ") : "solo link"}`).join(" · ");
      toast.success(`Richiesta creata — ${sent}`); onSent(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="sign-send-dialog">
        <DialogHeader><DialogTitle>Invia documenti</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2" data-testid="sign-mode">
            {[["firma", "Firma a distanza", "cliente e convenzionato firmano dal telefono"], ["stampa", "Solo PDF da stampare", "al convenzionato, che lo fa firmare su carta"]].map(([v, l, d]) => (
              <button key={v} type="button" onClick={() => setMode(v)} data-testid={`sign-mode-${v}`}
                className={`rounded-md border p-2.5 text-left transition-colors ${mode === v ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                <div className="font-medium">{l}</div><div className="text-xs text-muted-foreground">{d}</div>
              </button>
            ))}
          </div>
          {stampa && !practice.convenzionato_id && <p className="text-xs text-amber-600">La pratica non ha un convenzionato assegnato.</p>}
          <div className="space-y-2"><Label>{stampa ? "Documenti da inviare" : "Documenti da firmare"}</Label>
            {!tpls.length && <p className="text-xs text-amber-600">{stampa ? "Nessun modello ha campi posizionati." : "Nessun modello ha il campo «Firma cliente»: posizionalo prima in Prodotti → Modelli documenti."}</p>}
            {tpls.map((t) => (
              <div key={t.id} className="space-y-2">
                <label className="flex items-center gap-2"><Checkbox checked={sel.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`sign-tpl-${t.id}`} />{t.name}</label>
                {sel.includes(t.id) && t.consents?.length > 0 && (
                  <ConsentPicker consents={t.consents} value={cons[t.id] || {}} onChange={(v) => setCons({ ...cons, [t.id]: v })} testid={`sign-consents-${t.id}`} />
                )}
              </div>
            ))}
          </div>
          {!stampa && <>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1"><Label>Email cliente</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dall'anagrafica" data-testid="sign-client-email" /></div>
            <div className="space-y-1"><Label>Cellulare cliente</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="dall'anagrafica" data-testid="sign-client-phone" /></div>
          </div>
          <label className="flex items-center gap-2"><Checkbox checked={conv} disabled={!practice.convenzionato_id} onCheckedChange={(v) => setConv(!!v)} data-testid="sign-include-conv" />
            Firma anche il convenzionato {!practice.convenzionato_id && <span className="text-xs text-muted-foreground">(nessun convenzionato sulla pratica)</span>}</label>
          {conv && <p className="text-xs text-muted-foreground -mt-2">Dal suo link il convenzionato può far firmare anche cliente e coobbligato, se sono presenti.</p>}
          {tpls.some((t) => sel.includes(t.id) && t.fields.some((f) => f.key === "firma_coobbligato")) && (
            <div className="space-y-2 rounded-md border border-border p-3" data-testid="sign-coob-box">
              <label className="flex items-center gap-2"><Checkbox checked={coob} onCheckedChange={(v) => setCoob(!!v)} data-testid="sign-include-coob" />Firma anche il coobbligato</label>
              {coob && <>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input value={coobEmail} onChange={(e) => setCoobEmail(e.target.value)} placeholder="Email coobbligato (dai suoi dati)" data-testid="sign-coob-email" />
                  <Input value={coobPhone} onChange={(e) => setCoobPhone(e.target.value)} placeholder="Cellulare coobbligato (dai suoi dati)" data-testid="sign-coob-phone" />
                </div>
                <p className="text-xs text-muted-foreground">Il coobbligato è quello inserito nella scheda «Coobbligato» della pratica (o la seconda anagrafica collegata). Se non c'è, la sua firma viene saltata.</p>
              </>}
            </div>
          )}
          </>}
          <div className="space-y-2"><Label>Invia il link con</Label>
            <label className="flex items-center gap-2"><Checkbox checked={byEmail} onCheckedChange={(v) => setByEmail(!!v)} data-testid="sign-by-email" />Email automatica</label>
            <label className="flex items-center gap-2"><Checkbox checked={byWa} onCheckedChange={(v) => setByWa(!!v)} data-testid="sign-by-wa" />WhatsApp automatico</label>
            {byWa && <WaPicker wa={wa} setWa={setWa} preferred={stampa ? "documenti_stampa" : "documenti_firma"} />}
            <p className="text-xs text-muted-foreground">I link restano comunque copiabili dalla pratica. Validità 7 giorni.</p>
          </div>
          <Button className="w-full" onClick={send} disabled={busy || !tpls.length || (stampa && !practice.convenzionato_id)} data-testid="sign-send-btn"><Send className="h-4 w-4 mr-2" />{busy ? "Preparazione documenti…" : stampa ? "Invia PDF al convenzionato" : "Crea e invia"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ConsentsDialog = ({ r, tpls, onClose, onSaved }) => {
  const docs = r.docs_info.filter((d) => tpls.find((t) => t.id === d.template_id)?.consents?.length);
  const [tid, setTid] = useState(docs[0]?.template_id);
  const [vals, setVals] = useState(docs[0]?.consents || {});
  const [busy, setBusy] = useState(false);
  const pick = (id) => { setTid(id); setVals(docs.find((d) => d.template_id === id)?.consents || {}); };
  const save = async () => {
    setBusy(true);
    try { await api.post(`/sign-requests/${r.id}/consents`, { template_id: tid, consents: vals }); toast.success("Consensi aggiornati nel documento firmato"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="signed-consents-dialog">
        <DialogHeader><DialogTitle>Consensi sul documento firmato</DialogTitle></DialogHeader>
        {docs.length > 1 && (
          <Select value={tid} onValueChange={pick}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{docs.map((d) => <SelectItem key={d.template_id} value={d.template_id}>{d.name}</SelectItem>)}</SelectContent></Select>
        )}
        <ConsentPicker consents={tpls.find((t) => t.id === tid)?.consents} value={vals} onChange={setVals} testid="signed-consents" />
        <p className="text-xs text-muted-foreground">Il file firmato in pratica viene aggiornato con le caselle indicate; la versione firmata originale resta conservata.</p>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="signed-consents-save">{busy ? "Salvataggio…" : "Salva consensi"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RequestRow = ({ r, onCancel, tpls, onChanged }) => {
  const [consOpen, setConsOpen] = useState(false);
  const canCons = r.consents_editable && r.docs_info.some((d) => tpls.find((t) => t.id === d.template_id)?.consents?.length);
  const [label, cls] = STATUS[r.status] || [r.status, ""];
  return (
    <div className="space-y-2 px-3 py-2.5" data-testid={`sign-req-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm"><span className="font-medium">{r.documents.join(", ")}</span>
          <span className="ml-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} · {r.created_by}</span></div>
        <div className="flex items-center gap-2"><Badge className={cls} data-testid={`sign-req-status-${r.id}`}>{label}</Badge>
          {canCons && <Button size="sm" variant="outline" onClick={() => setConsOpen(true)} data-testid={`sign-req-consents-${r.id}`}>Consensi</Button>}
          {["in_attesa", "inviato", "caricato"].includes(r.status) && <Button size="sm" variant="ghost" onClick={() => onCancel(r)} data-testid={`sign-req-cancel-${r.id}`}><XCircle className="h-4 w-4 mr-1" />Annulla</Button>}</div>
      </div>
      {r.signers.map((s) => (
        <div key={s.role} className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="flex items-center gap-1.5">{s.signed_at ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Clock className="h-3.5 w-3.5 text-amber-600" />}
            <b className="capitalize">{s.role}</b> {s.name}{r.mode === "stampa" ? (s.sent.length ? ` · PDF inviato via ${s.sent.join(" e ")}` : " · link da inviare") : s.signed_at ? ` · firmato ${new Date(s.signed_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}${s.signed_by ? ` (${s.signed_by})` : ""}` : s.sent.length ? ` · inviato via ${s.sent.join(" e ")}` : " · link da inviare"}</span>
          {["in_attesa", "inviato", "caricato"].includes(r.status) && !s.signed_at && <Button size="sm" variant="outline" className="h-7" onClick={() => copy(s.link)} data-testid={`sign-copy-${r.id}-${s.role}`}><Copy className="h-3.5 w-3.5 mr-1" />Copia link</Button>}
        </div>
      ))}
      {r.uploads?.length > 0 && <p className="text-xs text-emerald-700" data-testid={`sign-req-uploads-${r.id}`}>Caricati dal convenzionato: {r.uploads.map((u) => u.name).join(", ")}</p>}
      {consOpen && <ConsentsDialog r={r} tpls={tpls} onClose={() => setConsOpen(false)} onSaved={onChanged} />}
    </div>
  );
};

export const SignRequestsCard = ({ practice, onSigned }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [tpls, setTpls] = useState([]);
  useEffect(() => { api.get("/doc-templates").then((r) => setTpls(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => api.get(`/practices/${practice.id}/sign-requests`).then((r) => setList(r.data)).catch(() => {}), [practice.id]);
  useEffect(() => { load(); }, [load]);
  const cancel = async (r) => { if (!window.confirm("Annullare la richiesta di firma? I link smetteranno di funzionare.")) return; await api.post(`/sign-requests/${r.id}/cancel`); load(); };
  const refresh = () => { load(); onSigned?.(); };
  return (
    <Card className="p-5 space-y-3" data-testid="sign-requests-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading font-semibold flex items-center gap-2"><PenLine className="h-4 w-4 text-primary" /> Firma a distanza</h2>
        <div className="flex gap-2">
          {list.length > 0 && <Button size="sm" variant="ghost" onClick={refresh} data-testid="sign-refresh-btn">Aggiorna</Button>}
          <Button size="sm" onClick={() => setOpen(true)} data-testid="sign-open-dialog-btn"><Send className="h-4 w-4 mr-1.5" />Invia documenti</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Cliente e convenzionato ricevono un link personale e firmano dal telefono; a firme completate il PDF firmato viene allegato alla pratica.</p>
      {list.length > 0 && <div className="divide-y divide-border rounded-md border border-border">{list.map((r) => <RequestRow key={r.id} r={r} onCancel={cancel} tpls={tpls} onChanged={refresh} />)}</div>}
      {open && <SendDialog practice={practice} onClose={() => setOpen(false)} onSent={load} />}
    </Card>
  );
};
